import { v4 as uuidv4 } from "uuid"
import {
  JOIN_AND_TRACK_TEMPLATE_ID,
  TDATA_ONBOARDING_TEMPLATE_ID,
} from "../../constants"

export type MassOpStatus =
  | "waiting"
  | "importing"
  | "launching"
  | "done"
  | "error"

export type MassOpBatchKind = "tdata" | "join"

export interface MassOpTwoFa {
  twoFaPassword?: string
  twoFaPhoneDigits?: number
}

export interface MassOpItem {
  key: string
  batchKind: MassOpBatchKind
  label: string
  /** tdata: файлы с короткими именами (как из корня tdata) */
  files?: File[]
  twoFa?: MassOpTwoFa
  /** join: нормализованная ссылка */
  link?: string
  clientId?: string
  role?: string
  status: MassOpStatus
  error?: string
  /** На какой фазе упало (для колонок Импорт/Запуск) */
  failedPhase?: "importing" | "launching"
  chainId?: string
}

export interface TdataStartItem {
  label: string
  files: File[]
  twoFa?: MassOpTwoFa
}

export interface JoinStartItem {
  link: string
  clientId?: string
  role?: string
}

export interface MassOpsSnapshot {
  items: readonly MassOpItem[]
  hasActive: boolean
  total: number
  completed: number
  failed: number
}

export interface MassOpsTransport {
  importTdata(body: FormData): Promise<{ id: string }>
  runTemplate(
    id: string,
    body: { inputs: Record<string, unknown>; clientId?: string; role?: string },
  ): Promise<{ id?: string }>
}

export const MASS_OPS_CONCURRENCY = 3

const ACTIVE_STATUSES: readonly MassOpStatus[] = [
  "waiting",
  "importing",
  "launching",
]

/**
 * Очередь массовых операций вне React: страницы приходят/уходят,
 * батч продолжает выполняться. Компоненты подписываются через
 * useSyncExternalStore (см. useMassOpsRunner.ts).
 * Ошибки нормализуются в transport (мосте): transport бросает Error
 * с человекочитаемым сообщением.
 */
export class MassOpsRunner {
  private items: MassOpItem[] = []
  private listeners = new Set<() => void>()
  private snapshot: MassOpsSnapshot = {
    items: [],
    hasActive: false,
    total: 0,
    completed: 0,
    failed: 0,
  }
  private unloadHandler: (() => void) | null = null

  constructor(private transport: MassOpsTransport) {}

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb)
    return () => {
      this.listeners.delete(cb)
    }
  }

  getSnapshot = (): MassOpsSnapshot => this.snapshot

  startTdataBatch(items: TdataStartItem[]): boolean {
    if (this.snapshot.hasActive) return false
    this.items = items.map((it) => ({
      key: uuidv4(),
      batchKind: "tdata",
      label: it.label,
      files: it.files,
      twoFa: it.twoFa,
      status: "waiting",
    }))
    this.pump()
    return true
  }

  startJoinBatch(pairs: JoinStartItem[]): boolean {
    if (this.snapshot.hasActive) return false
    this.items = pairs.map((p) => ({
      key: uuidv4(),
      batchKind: "join",
      label: p.link,
      link: p.link,
      clientId: p.clientId,
      role: p.role,
      status: "waiting",
    }))
    this.pump()
    return true
  }

  /**
   * Повтор только join-строк с ошибкой (упавший запрос цепочку не создал).
   * Для tdata повтор запрещён: после успешного импорта будет дубль клиента.
   */
  retryFailed(): boolean {
    if (this.snapshot.hasActive) return false
    const hasFailedJoin = this.items.some(
      (i) => i.batchKind === "join" && i.status === "error",
    )
    if (!hasFailedJoin) return false
    for (const it of this.items) {
      if (it.batchKind === "join" && it.status === "error") {
        it.status = "waiting"
        it.error = undefined
        it.failedPhase = undefined
        it.chainId = undefined
      }
    }
    this.pump()
    return true
  }

  clearFinished(): void {
    this.items = this.items.filter((i) => ACTIVE_STATUSES.includes(i.status))
    this.notify()
  }

  private pump(): void {
    let active = this.items.filter(
      (i) => i.status === "importing" || i.status === "launching",
    ).length
    for (const it of this.items) {
      if (active >= MASS_OPS_CONCURRENCY) break
      if (it.status !== "waiting") continue
      active++
      if (it.batchKind === "tdata") void this.runTdataItem(it)
      else void this.runJoinItem(it)
    }
    this.notify()
  }

  private async runTdataItem(it: MassOpItem): Promise<void> {
    it.status = "importing"
    it.failedPhase = "importing"
    this.notify()
    try {
      const formData = new FormData()
      for (const f of it.files ?? []) formData.append("files", f)
      if (it.twoFa?.twoFaPassword !== undefined)
        formData.append("twoFaPassword", it.twoFa.twoFaPassword)
      if (it.twoFa?.twoFaPhoneDigits !== undefined)
        formData.append("twoFaPhoneDigits", String(it.twoFa.twoFaPhoneDigits))
      const client = await this.transport.importTdata(formData)

      it.status = "launching"
      it.failedPhase = "launching"
      this.notify()
      const res = await this.transport.runTemplate(
        TDATA_ONBOARDING_TEMPLATE_ID,
        { inputs: {}, clientId: client.id },
      )
      it.chainId = res.id
      it.status = "done"
      it.failedPhase = undefined
    } catch (e) {
      it.status = "error"
      it.error = e instanceof Error ? e.message : String(e)
    }
    this.pump()
  }

  private async runJoinItem(it: MassOpItem): Promise<void> {
    it.status = "launching"
    it.failedPhase = "launching"
    this.notify()
    try {
      const res = await this.transport.runTemplate(JOIN_AND_TRACK_TEMPLATE_ID, {
        inputs: { link: it.link! },
        ...(it.clientId ? { clientId: it.clientId } : { role: it.role! }),
      })
      it.chainId = res.id
      it.status = "done"
      it.failedPhase = undefined
    } catch (e) {
      it.status = "error"
      it.error = e instanceof Error ? e.message : String(e)
    }
    this.pump()
  }

  private notify(): void {
    const active = this.items.some((i) => ACTIVE_STATUSES.includes(i.status))
    this.snapshot = {
      items: [...this.items],
      hasActive: active,
      total: this.items.length,
      completed: this.items.filter((i) => i.status === "done").length,
      failed: this.items.filter((i) => i.status === "error").length,
    }
    this.syncUnloadHandler(active)
    for (const cb of this.listeners) cb()
  }

  private syncUnloadHandler(active: boolean): void {
    if (active && !this.unloadHandler) {
      this.unloadHandler = (e?: BeforeUnloadEvent) => {
        if (e) {
          e.preventDefault()
          e.returnValue = ""
        }
      }
      window.addEventListener("beforeunload", this.unloadHandler)
    } else if (!active && this.unloadHandler) {
      window.removeEventListener("beforeunload", this.unloadHandler)
      this.unloadHandler = null
    }
  }
}
